import { io, Socket } from 'socket.io-client';
import { ensureFreshAccessToken } from './authTokens';

let socket: Socket | null = null;
const socketReadyCallbacks = new Set<(s: Socket) => void>();

function getSocketUrl(): string {
  // In development, the Vite proxy handles /socket.io routing
  // HTTP API paths must not become Socket.IO namespaces.
  if (import.meta.env.DEV) {
    return window.location.origin;
  }
  return new URL(import.meta.env.VITE_API_URL || '/', window.location.origin).origin;
}

/**
 * Auth payload for the Socket.IO handshake. The backend tries JWT
 * (`handshake.auth.token`) then falls back to a guest session token
 * (`handshake.auth.guestToken`). A converted/authenticated PWA user connects
 * with `token` so the backend joins them to their `user:<id>` room and tracks
 * presence — otherwise they're invisible to the nudge engine's online check and
 * never receive a realtime nudge.
 */
export type SocketAuth = { token: string } | { guestToken: string };

/**
 * Resolve handshake credentials from localStorage — invoked on EVERY
 * (re)connection attempt (function-form `auth`), never snapshotted, so a
 * reconnect after the access token's 15-minute TTL gets a refreshed JWT
 * instead of replaying a dead one until the retry budget is exhausted.
 *
 * Guest-first: an on-device guest session means active guest participation,
 * and guest thread rooms are only reachable on a guest-authed socket
 * (`join_thread` on a JWT socket checks the members table, which guests are
 * never in). A stored-but-stale JWT must not shadow it. Conversion removes
 * `guest_session`, so a converted user still connects with the JWT and gets
 * `user:<id>` + presence.
 */
async function resolveHandshakeAuth(): Promise<SocketAuth | Record<string, never>> {
  const raw = localStorage.getItem('guest_session');
  if (raw) {
    try {
      const session = JSON.parse(raw) as { sessionToken?: unknown; expiresAt?: unknown };
      if (
        typeof session.sessionToken === 'string' &&
        (typeof session.expiresAt !== 'string' ||
          new Date(session.expiresAt) > new Date())
      ) {
        return { guestToken: session.sessionToken };
      }
    } catch {
      // Corrupted entry — fall through to the JWT.
    }
  }
  const token = await ensureFreshAccessToken();
  return token ? { token } : {};
}

export function connectSocket(): Socket {
  // One app-wide instance. Because credentials are a provider rather than a
  // snapshot, the same instance stays valid across token refresh and
  // reconnects — it only needs recreating when the identity itself changes
  // (useSocket disconnects first, so this creates a fresh one).
  if (socket) return socket;

  socket = io(getSocketUrl(), {
    auth: (cb) => {
      void resolveHandshakeAuth().then((auth) => cb(auth));
    },
    withCredentials: true,
    transports: ['websocket', 'polling'],
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  socket.on('connect', () => {
    console.log('[Socket] Connected');
  });

  socket.on('disconnect', (reason) => {
    console.log('[Socket] Disconnected:', reason);
  });

  socket.on('connect_error', (err) => {
    console.warn('[Socket] Connection error:', err.message);
  });

  // Notify subscribers about the new instance. The set is NOT cleared:
  // onSocketReady subscriptions persist across socket recreations so consumer
  // listeners re-attach to every future instance (Socket.IO does not carry
  // listeners across a fresh socket).
  for (const cb of socketReadyCallbacks) {
    cb(socket);
  }

  return socket;
}

export function getSocket(): Socket | null {
  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}

export function joinThread(threadId: string): void {
  socket?.emit('join_thread', { threadId });
}

export function leaveThread(threadId: string): void {
  socket?.emit('leave_thread', { threadId });
}

/**
 * Subscribe to socket instances: fires immediately for the current socket (if
 * one exists) and again for EVERY future instance until unsubscribed — a
 * persistent subscription, not a one-shot. Consumers attach their listeners in
 * the callback and detach in the returned unsubscribe.
 */
export function onSocketReady(cb: (s: Socket) => void): () => void {
  socketReadyCallbacks.add(cb);
  if (socket) {
    cb(socket);
  }
  return () => { socketReadyCallbacks.delete(cb); };
}

export function isConnected(): boolean {
  return socket?.connected ?? false;
}
